import { useMemo, useState, useContext, useEffect, RefObject } from 'react';
import { makeStyles, createStyles, Button, Container } from '@material-ui/core';
import { Swipeable } from 'react-swipeable';
import React, { FC } from 'react';

import { MobilePageDefinition } from '../services/layout';

import { LayoutNodeProps, LayoutNodeWrapper } from './LayoutNode';
import { PagingControls } from './PagingControls';
import { FormContext } from '../FormContext';
import { Alert, AlertTitle } from '@material-ui/lab';

interface PageProps extends LayoutNodeProps {
  definition: MobilePageDefinition & { auditable?: boolean };
  activeCard: number;
  page: number;
  pageCount: number;
  pages: readonly {definition: MobilePageDefinition & { auditable?: boolean }, index: number, isVisible: boolean}[];
  onChangePage: (page: number) => void;
  pageOffsets: number[];
}

const useStyles = makeStyles(function(e) {
  return createStyles({
    root: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      // minHeight: '100%',
      overflow: 'hidden',
      height: '100%',
      position: 'relative',
      backgroundColor: e.palette.background.default
    },
    swipeable: {
      flex: 1,
      overflowY: 'scroll'
    },
    formContents: {
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    deskAuditPageMessage: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignContent: 'center',
      alignItems: 'center'
    },
    card: {},
    pagingControls: {
      zIndex: 10
    }
  })
});
export const Page: FC<PageProps> = React.memo(function(props) {
  const { definition, path, activeCard, page, pageCount, pages, onChangePage, pageOffsets } = props;

  const { isDeskAudit, toggleDeskAudit } = useContext(FormContext);

  const classes = useStyles(props);
  const [ cardOffsets, setCardOffsets ] = useState<number[]>([]);
  const [ showPage, setShowPage ] = useState<boolean>(false);

  // When used in verbiage, page title shouldn't include any icons
  const [ asciiTitle, setAsciiTitle ] = useState('');

  useEffect(() => {
    if (!definition) {
      return;
    }

    setAsciiTitle((definition.title || '').replace(/[^\x00-\x7F]/g, ""));

  }, [definition])

  const refs = useMemo(function e() {
    if (!definition) {
      return;
    }

    const refs: any[] = [];
    const offsets: number[] = [];

    definition.cards.forEach(function(card, i) {
      // fix typings
      const ref = React.createRef();
      if (card.excludeFromTableOfContents) {
        offsets.push(i);
      }
      refs.push(ref);
    });

    setCardOffsets(offsets);

    return refs;
  }, [definition]);

  const [ isVisible, setIsVisible ] = useState(true);

  const expMgr = useContext(FormContext).expMgr!;

  useEffect(function e() {
    if (!definition) {
      return;
    }
    let r;

    const condition = definition.visibility ?? definition.visible;

    if (condition !== undefined) {
      const subscription = expMgr.watch(condition).subscribe(function(newVisibility: any) {
        return setIsVisible(newVisibility);
      });
      return function() {
        return subscription.unsubscribe();
      }
    } else {
      setIsVisible(true);
    }
  }, [definition, expMgr]);

  useEffect(() => {
    if (definition.hasOwnProperty('auditable') && !definition?.auditable && isDeskAudit) {
      setShowPage(false);
    } else {
      setShowPage(true);
    }
  }, [definition, isDeskAudit])

  /**
   * Scroll a card into view, accounting for cards that have been hidden from the table of contents
   */
  useEffect(function() {
    if (!refs) {
      return;
    }
    let targetRef;

    if (activeCard === -1) {
      targetRef = refs![0];
    } else {

      const highestOffset = cardOffsets.findIndex(offset => {
        return activeCard >= offset;
      })

      if (!isNaN(activeCard + highestOffset)) {
        targetRef = refs![highestOffset + activeCard + 1];
      } else {
        targetRef = refs![activeCard];
      }
    }

    if (targetRef && targetRef.current) {
      (targetRef.current as any).scrollIntoView({
        behavior: 'auto'
      })
    }
  }, [activeCard, definition, cardOffsets, refs]);

  const swipeLeftAction = function() {
    if (page >= (pageCount - 1)) {
      return;
    }
    const highestOffset = pageOffsets.find(offset => {
      return page + 1 === offset;
    })

    let targetPage;

    if (highestOffset) {
      targetPage =  page + 2;
    } else {
      targetPage = page + 1;
    }
    onChangePage(targetPage);
  };

  const swipeRightAction = function() {
    if (page == 0) {
      return;
    }

    const highestOffset = pageOffsets.find(offset => {
      return page - 1 === offset;
    })

    let targetPage;

    if (highestOffset) {
      targetPage =  page - 2;
    } else {
      targetPage = page - 1;
    }

    if (targetPage >= 0) {
      onChangePage(targetPage);
    }
  };
  const pageControls = <PagingControls pageCount={pageCount} page={page} onChangePage={function(e) {onChangePage(e)}} pageOffsets = { pageOffsets }></PagingControls>;

  const swipeDistancePixels = 100;

  return isVisible && definition ? (
    <div className = { classes.root }>
      <div className={classes.pagingControls}>
        { pageControls }
      </div>
      <Swipeable
        onSwipedLeft = { swipeLeftAction }
        onSwipedRight = { swipeRightAction }
        className = { classes.swipeable }
        delta = { swipeDistancePixels }>
          <div className = { classes.formContents }>
            {
              showPage
                ? definition!.cards.map(function(_, index) {
                  return <div ref = { refs![index] } key = { index } className = { classes.card }>
                    <LayoutNodeWrapper path = { path.concat('/cards/') + index }></LayoutNodeWrapper>
                  </div>
                })
                : <div className={classes.deskAuditPageMessage}>
                    <Container fixed>
                      <Alert
                        severity="info"
                        action={
                          <Button color="inherit" size="small" onClick={ toggleDeskAudit }>
                            SWITCH
                          </Button>
                        }>
                        <AlertTitle>Desk evaluation in-progress</AlertTitle>
                        { asciiTitle } cannot be completed during a desk evaluation. To complete { asciiTitle }, switch to the On-Farm Evaluation.
                      </Alert>
                    </Container>
                  </div>
            }
          </div>
      </Swipeable>
      <div className={classes.pagingControls}>
        { pageControls }
      </div>
    </div>
  )
  : null
}, (previousProps, nextProps) => {
  return previousProps.page === nextProps.page &&
        previousProps.pages === nextProps.pages &&
        previousProps.activeCard === nextProps.activeCard;
});